import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

import MetaTags from '~/components/MetaTags/MetaTags';
import { initStore } from '~/state/store';
import { operations } from '~/state/pageData';
import { operations as uaOperations } from '~/state/userAgent';
import { operations as teamOperations } from '~/state/team';
import { bindActionCreators } from 'redux';
import withState from '~/hocs/withState';
import logEvent from '~/util/analytics';

import Layout from '~/components/Layout/Layout';
import BixbyLayout from '~/components/Bixby/BixbyLayout/BixbyLayout';
import TeamMiniSchedule from '~/components/TeamMiniSchedule/TeamMiniSchedule';
import ExtraInfoCard from '~/components/ExtraInfoCard/ExtraInfoCard';
import TeamBanner from '~/components/TeamBanner/TeamBanner';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';
import RelatedNewsRiver from '~/components/RelatedNewsRiver/RelatedNewsRiver';
import LandingOne from '~/components/Bixby/LandingOne/LandingOne';

import isBixbyUser from '~/util/isBixbyUser';
import getOr from '~/util/getOr';
import includes from 'lodash/includes';

import css from '~/pages/team.scss';

class Team extends React.Component {
  static async getInitialProps(props) {
    const teamId = parseInt(props.query.teamId, 10);
    const bixbyUser = props.isServer ? isBixbyUser(props.req.headers) : false;

    props.store.dispatch(uaOperations.storeBixbyFlag(bixbyUser));

    if (props.leagueSlug) {
      const teamResults = [
        await props.store.dispatch(teamOperations.setTeam(props.leagueSlug, teamId)),
        await props.store.dispatch(operations.setTeamSchedulePrevious(props.leagueSlug, teamId)),
        await props.store.dispatch(operations.setTeamScheduleCurrent(props.leagueSlug, teamId)),
        await props.store.dispatch(operations.setTeamScheduleUpcoming(props.leagueSlug, teamId)),
      ];

      if (!teamResults[0] || includes(teamResults[0].api_uri, '/error/events')) {
        // eslint-disable-next-line
        props.res.statusCode = 404;
      }

      return {
        bixbyUser,
        leagueSlug: props.leagueSlug,
      };
    }
    return {
      bixbyUser,
    };
  }

  componentDidMount() {
    this.props.loadRelatedNews({
      relatedUri: this.props.team.resource_uri,
      relatedNews: { before: null, relatedContent: [] },
    });

    logEvent('page_view', {
      league: getOr(this.props, 'leagueSlug', ''),
      page_type: 'team',
      page_name: getOr(this.props.team, 'id', null),
      team_id: getOr(this.props.team, 'id', null),
      channel: this.props.bixbyUser ? 'bixby' : 'web',
      page_url: getOr(this.props, 'url.asPath', ''),
    });
  }

  render() {
    const {
      leagueSlug,
      team,
      currentEvent,
      previousEvents,
      upcomingEvents,
      bixbyUser,
    } = this.props;

    const resourceUri = getOr(team, 'resource_uri', '');

    if (bixbyUser)
      return (
        <BixbyLayout>
          <Head>
            <style dangerouslySetInnerHTML={{ __html: css.stylesheet }} />
          </Head>
          <LandingOne leagueSlug={this.props.leagueSlug} section="teams" object={this.props.team} />
        </BixbyLayout>
      );

    return (
      <Layout>
        <Head>
          <style dangerouslySetInnerHTML={{ __html: css.stylesheet }} />
        </Head>
        <MetaTags content={team} type="team" />
        <div className={css.desktopView}>
          <Ad leagueSlug={leagueSlug} adType={AdTypes.LEADERBOARD} />
        </div>
        <TeamBanner team={team} />
        <TeamMiniSchedule
          currentEvent={currentEvent}
          previousEvents={previousEvents}
          upcomingEvents={upcomingEvents}
        />
        {resourceUri ? (
          <RelatedNewsRiver resourceUri={resourceUri} showTitle={false}>
            <div className={css.desktopView}>
              <Ad leagueSlug={leagueSlug} adType={AdTypes.BIGBOX_DESKTOP} />
              {team.team_extra_info != null &&
                team.team_extra_info.length > 0 && (
                  <div className={css.extraInfoCard}>
                    <ExtraInfoCard
                      extraInfo={team.team_extra_info}
                      title="Team Info"
                      showBackground
                    />
                  </div>
                )}
            </div>
          </RelatedNewsRiver>
        ) : null}
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  team: state.teamData.data,
  previousEvents: state.pageData.teamSchedulePrevious,
  currentEvent: state.pageData.teamScheduleCurrent,
  upcomingEvents: state.pageData.teamScheduleUpcoming,
});

const mapDispatchToProps = dispatch => ({
  loadRelatedNews: bindActionCreators(operations.loadRelatedNews, dispatch),
});

Team.propTypes = {
  leagueSlug: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  previousEvents: PropTypes.array.isRequired,
  currentEvent: PropTypes.array.isRequired,
  upcomingEvents: PropTypes.array.isRequired,
  loadRelatedNews: PropTypes.func.isRequired,
  bixbyUser: PropTypes.bool,
  url: PropTypes.object.isRequired,
};

Team.defaultProps = {
  bixbyUser: false,
};

Team.displayName = 'TeamPage';

export default withState({ createStore: initStore, mapDispatchToProps, mapStateToProps })(Team);
